import { DEFAULT_LANG } from '@dehaat/kisan-app-bl/constants/common'
import { CanceledError } from 'axios'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import {
  CACHE_EXPIRY_TIME,
  IS_PREBOOK_FLOW,
  PRODUCTS_QUERY_LIMIT,
} from '@/constants/common'
import ApiResponse from '@/models/ApiResponse'
import { Product, Product2, ProductsQueryType } from '@/models/Product'
import {
  clearProductResponse,
  setProductResponse,
} from '@/store/features/cacheSlice'
import { selectCurrentVendorId } from '@/store/features/locationSlice'
import fetchAllProducts from '@/utils/api/fetchAllProducts'
import { get } from '@/utils/axios'
import { fixNextPageUrl, getLocalStorageKey } from '@/utils/helper'
import transformProductInventorySet from '@/utils/helpers/transformProductInventorySet'

import { useAppSelector } from './reduxHooks'
import useInfiniteScroll from './useInfiniteScroll'
import useIsMobile from './useIsMobile'

type ParamValue = string | string[] | number | number[]

interface Params {
  vendor_id: string | number
  page_size: number
  brand_id: ParamValue
  crop_id: ParamValue
  category_id: ParamValue
  collection_id: ParamValue
  lang: string
  isprebook: boolean
}

type ParamKeys = keyof Params

export const createSearchProductQuery = (params: Params) => {
  return Object.keys(params)
    .filter((key): key is ParamKeys => {
      const paramKey = key as ParamKeys
      const value = params[paramKey]
      if (Array.isArray(value)) {
        return value.length > 0
      }
      return value != null
    })
    .flatMap((key) => {
      const paramKey = key as ParamKeys
      const value = params[paramKey]
      if (Array.isArray(value)) {
        return value.map((val) => `${paramKey}=${val}`)
      }
      return [`${paramKey}=${value}`]
    })
    .join('&')
}

const useSearchProducts = (
  searchWithoutFilters = false,
  limit = PRODUCTS_QUERY_LIMIT,
  cropId?: string,
  categoryId?: string,
  brandId?: string,
) => {
  const { query, locale } = useRouter()
  const currentVendorId = useAppSelector(selectCurrentVendorId)
  const {
    b: encodedBrands,
    c: encodedCategories,
    cr: encodedCrops,
    cl: encodedCollections,
    s: encodedSearchQuery,
  }: ProductsQueryType = query
  const [nextPageUrl, setNextPageUrl] = useState('')
  const [searchedProducts, setSearchedProducts] = useState<Product[]>([])
  const [searchingProducts, setSearchingProducts] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const totalCountRef = useRef<number | null>(null)
  const { isMobile } = useIsMobile()
  const effectiveLimit = isMobile ? limit : limit * 2
  const isprebook = !!getLocalStorageKey(IS_PREBOOK_FLOW)

  const cache = useAppSelector((state) => state.cache.fetchProducts)
  const dispatch = useDispatch()

  const onSearchParamsChanged = useCallback(
    async (
      encodedSearchQuery: string,
      encodedBrands: string,
      encodedCategories: string,
      encodedCrops: string,
      encodedCollections: string,
      limit: number,
      vendorId: string | null,
    ) => {
      setSearchingProducts(true)
      try {
        const categoryIds = categoryId
          ? categoryId
          : encodedCategories
            ? encodedCategories.split('-')
            : []

        const cropIds = cropId
          ? cropId
          : encodedCrops
            ? encodedCrops.split('-')
            : []

        const brandIds = brandId
          ? brandId
          : encodedBrands
            ? encodedBrands.split('-')
            : []
        const collectionIds = encodedCollections
          ? encodedCollections.split('-')
          : []
        if (vendorId) {
          const params: Params = {
            vendor_id: vendorId,
            page_size: limit,
            brand_id: brandIds,
            crop_id: cropIds,
            category_id: categoryIds,
            collection_id: collectionIds,
            lang: locale || DEFAULT_LANG,
            isprebook,
          }

          const query: string = createSearchProductQuery(params)
          const key = JSON.stringify(query)
          let response
          let fromCache = false
          if (cache && cache[key]) {
            response = cache[key]
            fromCache = true
          } else {
            response = await fetchAllProducts({ query, abortRef })
          }
          const products = response.results.map((product: Product2) =>
            transformProductInventorySet(product),
          )
          setSearchedProducts([...products])
          totalCountRef.current = response.count
          setNextPageUrl(fixNextPageUrl(response.next) || '')
          if (!fromCache) {
            dispatch(
              setProductResponse({
                [key]: response,
              }),
            )
            setTimeout(() => {
              dispatch(clearProductResponse(key))
            }, CACHE_EXPIRY_TIME)
          }
          setSearchingProducts(false)
        }
      } catch (e) {
        if (!(e instanceof CanceledError)) {
          setSearchingProducts(false)
        }
      }
    },
    [categoryId, cropId, brandId, locale, isprebook],
  )

  const { infiniteScrollRef } = useInfiniteScroll(
    useCallback(async () => {
      if (nextPageUrl) {
        setSearchingProducts(true)
        try {
          let response
          let fromCache = false
          if (cache && cache[nextPageUrl]) {
            response = cache[nextPageUrl]
            fromCache = true
          } else {
            response = await get<ApiResponse<Product2>>(nextPageUrl)
          }
          if (response?.results) {
            const products = response.results.map((product: Product2) =>
              transformProductInventorySet(product),
            )
            setSearchedProducts((prevProducts) => [
              ...prevProducts,
              ...products,
            ])
            setNextPageUrl(fixNextPageUrl(response.next) || '')
            if (!fromCache) {
              dispatch(setProductResponse({ [nextPageUrl]: response }))
              setTimeout(() => {
                dispatch(clearProductResponse(nextPageUrl))
              }, CACHE_EXPIRY_TIME)
            }
            setSearchingProducts(false)
          }
        } catch (e) {
          if (!(e instanceof CanceledError)) {
            setSearchingProducts(false)
          }
        }
      }
    }, [nextPageUrl, setNextPageUrl]),
  )

  useEffect(() => {
    setSearchedProducts([])
    setNextPageUrl('')
    if (
      encodedSearchQuery ||
      encodedBrands ||
      encodedCategories ||
      encodedCrops ||
      encodedCollections ||
      searchWithoutFilters
    ) {
      if (abortRef.current) {
        abortRef.current.abort()
      }
      if ('AbortController' in window) {
        abortRef.current = new AbortController()
      }
      onSearchParamsChanged(
        encodedSearchQuery as string,
        encodedBrands as string,
        encodedCategories as string,
        encodedCrops as string,
        encodedCollections as string,
        effectiveLimit,
        currentVendorId ? currentVendorId.toString() : null,
      )
    }
  }, [
    encodedSearchQuery,
    encodedBrands,
    encodedCategories,
    encodedCrops,
    encodedCollections,
    effectiveLimit,
    searchWithoutFilters,
    onSearchParamsChanged,
    currentVendorId,
  ])

  return {
    appliedBrandIds: encodedBrands ? encodedBrands.split('-') : [],
    appliedCategoryIds: encodedCategories ? encodedCategories.split('-') : [],
    appliedCropIds: encodedCrops ? encodedCrops.split('-') : [],
    appliedCollectionIds: encodedCollections
      ? encodedCollections.split('-')
      : [],
    infiniteScrollRef,
    searchedProducts,
    searchingProducts,
    searchQuery: encodedSearchQuery
      ? decodeURIComponent(encodedSearchQuery)
      : '',
    totalCount: totalCountRef.current,
  }
}

export default useSearchProducts
